<template>
  <div class="slw" :class="`slw--${viz}`">
    <div v-if="!data && loading" class="slw-skeleton"></div>
    <p v-else-if="error && !data" class="ov-empty">{{ $t('dash.overview.error') }}</p>
    <p v-else-if="data && !data.enabled" class="ov-empty">{{ $t('dash.overview.widgets.short_links.disabled') }}</p>
    <p v-else-if="!visible" class="ov-empty">{{ $t('dash.overview.widgets.short_links.no_data') }}</p>

    <!-- Tuiles : clics humains (avec évolution) et passages de robots -->
    <div v-else-if="viz === 'tile'" class="slw-tiles">
      <div v-for="tile in tiles" :key="tile.key" class="slw-tile">
        <Tooltip :text="tile.help || ''" :active="!!tile.help" position="top">
          <span class="slw-label" :class="{ 'slw-label--help': tile.help }">{{ tile.label }}</span>
        </Tooltip>
        <Tooltip :text="tile.exact" :active="!!tile.exact" position="top">
          <span class="slw-value">{{ tile.display }}</span>
        </Tooltip>
        <span v-if="tile.trend" class="trend-badge" :class="tile.trend.cls">
          <i class="material-icons">{{ tile.trend.icon }}</i>{{ tile.trend.text }}
          <small>vs {{ tile.prevDisplay }}</small>
        </span>
      </div>
    </div>

    <!-- Clics par jour -->
    <div v-else-if="viz === 'chart'" class="slw-chart">
      <Graph v-if="chart.labels.length > 1" :id="`slw-${uid}`" :key="chartKey" :data="chart.data" :labels="chart.labels" height="100%" width="100%" />
      <p v-else class="ov-empty">{{ $t('dash.overview.widgets.no_series') }}</p>
    </div>

    <!-- Classement par page, réseau ou publication -->
    <ol v-else-if="viz === 'ranking'" class="slw-ranking">
      <li v-for="(row, idx) in ranking" :key="row.id">
        <span class="slw-rank">{{ idx + 1 }}</span>
        <Tooltip :text="row.title || ''" :active="!!row.title" position="top" class="slw-rank-name">
          <span>{{ row.name }}</span>
        </Tooltip>
        <span class="slw-rank-bar"><span :style="{ width: `${row.share}%` }"></span></span>
        <strong>{{ row.display }}</strong>
      </li>
    </ol>
  </div>
</template>

<script>
import moment from 'moment'
import Graph from '../../../reporting-web/Graph.vue'
import Tooltip from '../../../tooltip/tooltipInit.vue'
import { apiGet, cachedGet, rangeParams, compact, PALETTE } from './widgetFetch'
import { shortLinksVisible } from '@/utils/shortLinkMetrics'
import { shortLinkUrl } from '@/utils/shortLinks'

let uidCounter = 0

/**
 * Widget « Clics sur les liens » : clics des liens courts mws.li sur les pages du tableau de bord
 * options : viz (tile | chart | ranking), groupBy (page | network | post), top
 */
export default {
  name: 'ShortLinksWidget',

  components: { Graph, Tooltip },

  props: {
    options: { type: Object, default: () => ({}) },
    pageIds: { type: Array, default: null },
    pages: { type: Array, default: () => [] },
    range: { type: Object, required: true },
    refreshToken: { type: Number, default: 0 }
  },

  data() {
    return { uid: ++uidCounter, data: null, loading: false, error: false }
  },

  computed: {
    viz() {
      return this.options.viz || 'tile'
    },

    path() {
      return `/reporting/short-links?${rangeParams(this.range, { pages: this.pageIds })}`
    },

    visible() {
      return shortLinksVisible(this.data)
    },

    tiles() {
      const d = this.data || {}
      return [
        {
          key: 'clicks', label: this.$t('dash.reporting.short_links.clicks'), help: this.$t('dash.reporting.tooltip.short_links_clicks'),
          display: compact(d.clicks || 0), exact: (d.clicks || 0) >= 10000 ? Number(d.clicks).toLocaleString() : '',
          prevDisplay: compact(d.previous_clicks), trend: this.trend(d.clicks || 0, d.previous_clicks)
        },
        {
          key: 'bot_clicks', label: this.$t('dash.reporting.short_links.bot_clicks'), help: this.$t('dash.reporting.tooltip.short_links_bot_clicks'),
          display: compact(d.bot_clicks || 0), exact: (d.bot_clicks || 0) >= 10000 ? Number(d.bot_clicks).toLocaleString() : ''
        }
      ]
    },

    chartKey() {
      return `${this.path}-${this.viz}`
    },

    chart() {
      const days = ((this.data && this.data.by_day) || []).slice().sort((a, b) => (a.day < b.day ? -1 : 1))
      return {
        labels: days.map(d => moment(d.day).format('DD/MM')),
        data: [
          { label: this.$t('dash.reporting.short_links.clicks'), data: days.map(d => d.clicks || 0), type: 'bar', color: PALETTE[0] },
          { label: this.$t('dash.reporting.short_links.bot_clicks'), data: days.map(d => d.bot_clicks || 0), type: 'line', color: '#9ba3b7' }
        ]
      }
    },

    // Clics ventilés par page, par réseau ou par publication (top liens), triés par valeur
    breakdown() {
      const d = this.data || {}
      if (this.options.groupBy === 'network') {
        return (d.by_network || []).map(row => ({ id: row.network, name: this.$t(`dash.overview.network.${row.network}`), value: row.clicks || 0 }))
      }
      if (this.options.groupBy === 'post') {
        return (d.top_links || []).map(link => ({
          id: link.code,
          name: shortLinkUrl(link).replace(/^https?:\/\//, ''),
          title: link.url,
          value: link.clicks || 0
        }))
      }
      // page_id 'unknown' : lien rattaché à aucune page connue (regroupé sous « Autres »)
      return (d.by_page || []).map(row => ({
        id: row.page_id,
        name: row.page_id === 'unknown' ? this.$t('dash.overview.groups.others') : this.pageName(row.page_id),
        value: row.clicks || 0
      }))
    },

    ranking() {
      const rows = this.breakdown.filter(r => r.value > 0).sort((a, b) => b.value - a.value).slice(0, this.options.top || 5)
      const max = Math.max(1, ...rows.map(r => r.value))
      return rows.map(r => ({ ...r, display: compact(r.value), share: Math.round(r.value / max * 100) }))
    }
  },

  watch: {
    path() {
      this.load()
    },

    refreshToken() {
      this.load(true)
    }
  },

  created() {
    this.load()
  },

  methods: {
    async load(refresh = false) {
      const path = this.path
      const cached = cachedGet(path)
      if (cached) this.data = cached
      this.loading = true
      this.error = false
      try {
        const body = await apiGet(path, { refresh })
        if (path === this.path) this.data = body
      } catch (e) {
        if (path === this.path) this.error = true
      } finally {
        if (path === this.path) this.loading = false
      }
    },

    pageName(id) {
      const page = this.pages.find(p => String(p.id) === String(id))
      return (page && page.name) || id
    },

    trend(value, prev) {
      if (!prev) return null
      const delta = (value - prev) / Math.abs(prev) * 100
      const rounded = Math.round(delta * 10) / 10
      if (!rounded) return { cls: 'flat', icon: 'trending_flat', text: '0%' }
      return rounded > 0
        ? { cls: 'up', icon: 'trending_up', text: `+${rounded}%` }
        : { cls: 'down', icon: 'trending_down', text: `${rounded}%` }
    }
  }
}
</script>

<style scoped>
.slw {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.slw-skeleton {
  flex: 1;
  border-radius: 6px;
  background: #f1edf5;
}
.slw-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
  align-content: start;
}
.slw-tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.slw-label {
  font-size: 12px;
  font-weight: 500;
  color: #555e75;
}
.slw-label--help {
  cursor: help;
}
.slw-value {
  font-size: 26px;
  font-weight: 700;
  color: #121826;
  line-height: 1.15;
}
.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
}
.trend-badge .material-icons {
  font-size: 14px;
}
.trend-badge small {
  margin-left: 4px;
  font-weight: 500;
  color: #9ba3b7;
}
.trend-badge.up { background: #e6f9f0; color: #1a9e5e; }
.trend-badge.down { background: #fdf2f2; color: #d93025; }
.trend-badge.flat { background: #f1edf5; color: #555e75; }
.slw-chart {
  position: relative;
  flex: 1;
  min-height: 0;
}
.slw-ranking {
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
}
.slw-ranking li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 12.5px;
  color: #121826;
}
.slw-rank {
  width: 18px;
  font-weight: 700;
  color: #ab6e93;
  text-align: center;
}
.slw-rank-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.slw-rank-bar {
  width: 30%;
  height: 6px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.slw-rank-bar span {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #6b1f4d;
}
</style>
